import React, { ReactElement, useState } from 'react'
import './FormStyles.scss'
import { useForm, SubmitHandler } from 'react-hook-form'
import type { LabelConfig } from '../../App'
import fetchLabels from '../../api/fetchLabels'
import type { Clip } from '../video_player/VideoPlayer'
import FormButtons from './FormButtons'
import FormValues from './FormValues'

function hasAnyTrueValue(data: Record<string, boolean>): boolean {
  return Object.values(data).some(value => value === true)
}

export default function LabelForm({
  labelConfig,
  currentClip,
  loginStatus,
  setCurrentClip,
  setPreviousClip,
}: {
  labelConfig: LabelConfig
  currentClip: Clip | null
  loginStatus: boolean
  setCurrentClip: React.Dispatch<React.SetStateAction<Clip | null>>
  setPreviousClip: React.Dispatch<React.SetStateAction<Clip | null>>
}): ReactElement {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<typeof labelConfig.labelFormModel>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  })

  const onSubmit: SubmitHandler<typeof labelConfig.labelFormModel> = async data => {
    if (loginStatus && currentClip && !isSubmitting) {
      if (!hasAnyTrueValue(data)) {
        return // Prevent submission if no radio option is selected
      }

      setIsSubmitting(true)
      try {
        await fetchLabels('POST', data, currentClip)

        // eslint-disable-next-line no-param-reassign
        currentClip.labels = data
        setPreviousClip(currentClip)
        setCurrentClip(null)
        reset()
      } catch (err) {
        console.error(err)
        // Optionally handle error (show message to user, etc.)
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  const radioOptions: ReactElement[] = []
  Object.keys(labelConfig.labels).forEach((group: string) => {
    radioOptions.push(
      <div className="radioOptions" key={group}>
        <FormValues
          register={register}
          values={labelConfig.labels[group]}
          labelInfo={labelConfig.labelInfo}
          currentClip={currentClip}
        />
        {errors[group] && <span className="error-message">Please select an option for {group}</span>}
      </div>,
    )
  })

  const buttons: ReactElement[] = []
  labelConfig.issueNames.forEach((issue: string, index: number) => {
    buttons.push(
      <FormButtons
        level={index}
        name={issue}
        register={register}
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
        setValue={setValue}
        reset={reset}
        key={issue}
        defaultValues={labelConfig.labelFormModel}
      />,
    )
  })

  return (
    <div className="labelForm">
      <form onSubmit={handleSubmit(onSubmit)}>
        {radioOptions}
        <div className="formButtons">
          <input type="submit" className="submitButton" disabled={isSubmitting} />
          {buttons}
        </div>
      </form>
    </div>
  )
}
